/**
 * Where the money is, in one vocabulary.
 *
 * `Transaction.status` means different things depending on who reported the
 * sale: Olive maps money-first to auth/captured/failed, Wildfire compares its
 * status to 'approved' — a value Wildfire never sends — so every Wildfire row
 * lands on 'pending', and Access Travel writes captured/voided outright. Read
 * down a column, those values are not comparable, which is the one thing an
 * audit column has to be.
 *
 * These four states track the money rather than the vendor:
 *
 *   pending   still with the vendor; recorded, not paid to us
 *   received  the vendor has paid us
 *   distributed  we have paid the school its share
 *   refunded  the purchase was reversed after the fact
 *   failed    it failed on the way to us
 *
 * The vendor's own status decides everything except `distributed`, which is
 * ours and comes from the SchoolPaymentStatement for that school and month.
 *
 * `apps/core/funnel.py` is the authoritative mapping and serves the state as
 * `funnel_status` on the transaction feed; this module prefers that field and
 * keeps the tables below only as a fallback for a client talking to an API that
 * predates it. The two must stay in step.
 */

export const PENDING = 'pending';
export const RECEIVED = 'received';
export const DISTRIBUTED = 'distributed';
export const REFUNDED = 'refunded';
export const FAILED = 'failed';

/**
 * Colour and icon deliberately mirror PAYMENT_STATE in ./billing so
 * BillingStatusIcon renders these unchanged and a green check means the same
 * thing on every page. Held as literals rather than imported: billing.js needs
 * the funnel to decide what is billable, and importing back the other way makes
 * a cycle in which whichever module loads second sees the other half-built.
 */
export const FUNNEL_STATE = {
  [PENDING]: {
    key: PENDING,
    label: 'Pending',
    description: 'With the vendor — not paid to us yet',
    color: '#c99700',
    icon: 'hourglass',
  },
  [RECEIVED]: {
    key: RECEIVED,
    label: 'Received',
    description: 'The vendor has paid us',
    color: '#1e7e34',
    icon: 'bank',
  },
  [DISTRIBUTED]: {
    key: DISTRIBUTED,
    label: 'Distributed',
    description: 'Paid out to the school',
    color: '#1e7e34',
    icon: 'check',
  },
  [REFUNDED]: {
    key: REFUNDED,
    label: 'Refunded',
    description: 'The purchase was reversed',
    color: '#b00020',
    // The circular arrow reads as "went back"; the alert below is kept for the
    // case where money never arrived at all. Same red, different cause.
    icon: 'processing',
  },
  [FAILED]: {
    key: FAILED,
    label: 'Failed',
    description: 'Failed on the way to us',
    color: '#b00020',
    icon: 'alert',
  },
};

/**
 * Olive's reward lifecycle.
 *
 * `distributed_to_member_distributor` is the one event that means the money
 * reached us. `distributed_to_publisher` and `distributed_to_olive` are Olive's
 * internal legs — a transaction can end on `distributed_to_olive` and still be
 * money we have not been paid.
 */
export const OLIVE_FUNNEL = {
  pending_merchant_approval: PENDING,
  pending_merchant_funding: PENDING,
  earned_pending_settlement: PENDING,
  distributed_to_publisher: PENDING,
  distributed_to_olive: PENDING,
  distributed_to_member_distributor: RECEIVED,
  completed: RECEIVED,
};

/** Wildfire re-sends the same commission as it advances. Only PAID is money. */
export const WILDFIRE_FUNNEL = {
  PENDING: PENDING,
  READY: PENDING,
  CONFIRMED: PENDING,
  PAID: RECEIVED,
  // Never qualified in the first place, versus taken back after the fact.
  DISQUALIFIED: FAILED,
  REMOVED: REFUNDED,
};

/**
 * Olive has no failure enum — the ingest tests for substrings, so a vendor
 * variant we have never seen still resolves. The token list is the union of the
 * five in `olive_reward_status_has_failed` (homecrowd/views.py) plus 'refund',
 * split by cause: a reversal is money that arrived and went back, a failure is
 * money that never arrived. Reversal is checked first so 'refund_failed' reads
 * as a reversal rather than a failure.
 */
const REVERSAL_TOKENS = ['refund', 'cancel', 'void'];
const FAILURE_TOKENS = ['reject', 'declin', 'fail'];

const hasToken = (value, tokens) => {
  const v = String(value || '').toLowerCase();
  return Boolean(v) && tokens.some((token) => v.includes(token));
};

export const isReversalStatus = (value) => hasToken(value, REVERSAL_TOKENS);
export const isFailureStatus = (value) => hasToken(value, FAILURE_TOKENS);

/** Either way the money is not ours — used to keep the two checks together. */
export const isDeadStatus = (value) =>
  isReversalStatus(value) || isFailureStatus(value);

/** What `Transaction.status` implies for sources with no vendor lifecycle. */
const FALLBACK = {
  failed: FAILED,
  // A voided Access Travel row is a cancelled booking — reversed, not failed.
  voided: REFUNDED,
  refunded: REFUNDED,
  captured: RECEIVED,
  auth: PENDING,
  // Not a member of Transaction.STATUS_CHOICES, but 225 production rows hold
  // it — see the Wildfire note above.
  pending: PENDING,
};

/**
 * The funnel state for one transaction.
 *
 * `statement` is the SchoolPaymentStatement covering this transaction's school
 * and month, or null. Precedence: a failure is terminal, then a sent statement,
 * then whatever the vendor says.
 *
 * A statement can be marked sent for a month holding money the vendor has not
 * remitted, so `sent` can sit above a vendor status of `pending`. That is a
 * real situation worth seeing rather than an error to hide.
 */
export const funnelStatusFor = (txn, statement = null) => {
  // The server derives this from the same tables and knows the payout state
  // without being handed a statement, so it wins whenever it is present.
  const served = txn && txn.funnel_status;
  if (served && FUNNEL_STATE[served]) {
    // ...except that a statement the caller holds may have been sent since the
    // feed was fetched, which only the caller can know.
    if (statement && statement.is_sent && served !== REFUNDED && served !== FAILED) {
      return DISTRIBUTED;
    }
    return served;
  }

  const vendor = txn && txn.vendor_status;

  const stored = txn && txn.status;
  if (isReversalStatus(vendor) || isReversalStatus(stored)) return REFUNDED;
  if (isFailureStatus(vendor) || isFailureStatus(stored)) return FAILED;

  if (statement && statement.is_sent) return DISTRIBUTED;

  if (vendor) {
    const mapped = OLIVE_FUNNEL[vendor] || WILDFIRE_FUNNEL[String(vendor).toUpperCase()];
    if (mapped) return mapped;
  }

  // No vendor lifecycle (Access Travel, manual, receipts): the stored status is
  // written final, so it is the best signal available.
  return FALLBACK[String(stored || '').toLowerCase()] || PENDING;
};

/**
 * Has the vendor actually paid us for this transaction?
 *
 * Deliberately evaluated with no statement, so the answer does not change once
 * a payout is marked sent — School Pay needs "money we hold" to stay true of a
 * transaction after it has been paid out, or a sent month would empty itself.
 */
/**
 * Did the vendor ever report this as worth anything?
 *
 * False only when every single delivery carried a zero commission — the vendor
 * recorded the order and pays nothing on it. Those are kept off the money pages
 * and off Audit entirely; they are not evidence of anything.
 */
export const everWorthSomething = (txn) =>
  txn == null || txn.ever_nonzero !== false;

/**
 * The most recent figures the vendor reported. A returned order reads $0.00,
 * because that is what it is now worth. Used by Audit.
 */
export const currentMoney = (txn) => ({
  amount: txn?.current_amount != null ? Number(txn.current_amount) : Number(txn?.amount || 0),
  commission:
    txn?.current_commission != null
      ? Number(txn.current_commission)
      : Number(txn?.commission_amount || 0),
});

/**
 * The last non-zero figures: what the order was worth before it came back.
 * Used by the transaction lists, where a return should still show its size.
 */
export const fullMoney = (txn) => ({
  amount: txn?.full_amount != null ? Number(txn.full_amount) : Number(txn?.amount || 0),
  commission:
    txn?.full_commission != null
      ? Number(txn.full_commission)
      : Number(txn?.commission_amount || 0),
});

export const isReceivedTxn = (txn) => {
  // A commission the vendor never valued cannot be invoiced or paid out, even
  // though it did technically arrive.
  if (!everWorthSomething(txn)) return false;
  const state = funnelStatusFor(txn, null);
  // `distributed` is money we received and have since paid on; it must still
  // count, or a sent month would empty itself off the School Pay list.
  return state === RECEIVED || state === DISTRIBUTED;
};

/** The state object rather than the key, for rendering. */
export const funnelStateFor = (txn, statement = null) =>
  FUNNEL_STATE[funnelStatusFor(txn, statement)];

export const ALL_FUNNEL = 'all';
/** The default: money that is still real. Replaces EXCLUDE_NON_BILLABLE. */
export const EXCLUDE_DEAD = 'exclude_dead';

export const FUNNEL_FILTER_OPTIONS = [
  { value: EXCLUDE_DEAD, label: 'All except refunded & failed' },
  { value: ALL_FUNNEL, label: 'All statuses' },
  { value: PENDING, label: 'Pending' },
  { value: RECEIVED, label: 'Received' },
  { value: DISTRIBUTED, label: 'Distributed' },
  { value: REFUNDED, label: 'Refunded' },
  { value: FAILED, label: 'Failed' },
];

export const matchesFunnelStatus = (txn, filter, statement = null) => {
  if (filter === ALL_FUNNEL) return true;
  const state = funnelStatusFor(txn, statement);
  if (filter === EXCLUDE_DEAD) return state !== REFUNDED && state !== FAILED;
  return state === filter;
};
